<template>
  <div class="design-swiss">
    <header class="swiss-header">
      <img :src="logo" alt="BOX21" class="swiss-header__logo" />
      <nav>
        <router-link to="/designs">Designs</router-link>
        <button type="button" @click="goToLogin">Login</button>
      </nav>
    </header>

    <section class="swiss-hero">
      <div class="swiss-hero__grid">
        <div class="swiss-hero__title">
          <span class="swiss-hero__num">01</span>
          <h1>{{ hero.line1 }}</h1>
        </div>
        <div class="swiss-hero__title">
          <span class="swiss-hero__num">02</span>
          <h1>{{ hero.line2 }}</h1>
        </div>
        <div class="swiss-hero__title swiss-hero__title--accent">
          <span class="swiss-hero__num">03</span>
          <h1>{{ hero.line3 }}</h1>
        </div>
      </div>
      <div class="swiss-hero__rule" />
      <p class="swiss-hero__tagline">{{ hero.tagline }}</p>
    </section>

    <section class="swiss-grid">
      <div v-for="(pillar, i) in pillars" :key="pillar.id" class="swiss-cell">
        <span class="swiss-cell__index">{{ String(i + 1).padStart(2, '0') }}</span>
        <h2>{{ pillar.title }}</h2>
        <ul>
          <li v-for="tag in pillar.tags" :key="tag">{{ tag }}</li>
        </ul>
      </div>
    </section>

    <section class="swiss-showcase">
      <article v-for="item in showcase" :key="item.id">
        <img :src="item.image" :alt="item.title" />
        <h3>{{ item.title }}</h3>
        <p>{{ item.text }}</p>
      </article>
    </section>

    <footer v-if="showTrustedBy" class="swiss-footer">
      <span>Trusted by</span>
      <div class="swiss-footer__logos">
        <img v-for="p in partners" :key="p.name" :src="p.logo" :alt="p.name" />
      </div>
    </footer>
  </div>
</template>

<script>
import { BRAND, HERO, PILLARS, SHOWCASE, PARTNERS, SHOW_TRUSTED_BY } from '@/components/front_page/designs/shared'

export default {
  name: 'DesignSwiss',
  data() {
    return {
      logo: BRAND.logo,
      hero: HERO,
      pillars: PILLARS,
      showcase: SHOWCASE,
      partners: PARTNERS,
      showTrustedBy: SHOW_TRUSTED_BY,
    }
  },
  methods: {
    goToLogin() { this.$router.push('/login') },
  },
}
</script>

<style scoped>
.design-swiss {
  min-height: 100vh;
  background: #fff;
  color: #111;
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
}

.swiss-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 32px;
  border-bottom: 3px solid #111;
}

.swiss-header__logo {
  height: 36px;
}

.swiss-header nav {
  display: flex;
  gap: 24px;
  align-items: center;
}

.swiss-header a,
.swiss-header button {
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: #111;
  text-decoration: none;
  background: none;
  border: none;
  cursor: pointer;
  font-family: inherit;
}

.swiss-hero {
  padding: 64px 32px;
  max-width: 1200px;
  margin: 0 auto;
}

.swiss-hero__grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}

.swiss-hero__title {
  border-left: 4px solid #0677b9;
  padding-left: 20px;
}

.swiss-hero__title--accent {
  border-left-color: #7bacd6;
}

.swiss-hero__num {
  font-size: 0.75rem;
  color: #0677b9;
  font-weight: 700;
  letter-spacing: 0.1em;
}

.swiss-hero h1 {
  font-size: clamp(1.25rem, 2.5vw, 1.75rem);
  font-weight: 700;
  margin: 8px 0 0;
  line-height: 1.2;
  text-transform: uppercase;
  letter-spacing: -0.01em;
}

.swiss-hero__rule {
  height: 3px;
  background: #111;
  margin: 48px 0 24px;
}

.swiss-hero__tagline {
  font-size: 1rem;
  line-height: 1.7;
  max-width: 560px;
  margin: 0;
  color: #444;
}

.swiss-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  border-top: 3px solid #111;
  border-bottom: 3px solid #111;
}

.swiss-cell {
  padding: 32px 24px;
  border-right: 1px solid #111;
}

.swiss-cell:last-child {
  border-right: none;
}

.swiss-cell__index {
  font-size: 2.5rem;
  font-weight: 200;
  color: #0677b9;
  line-height: 1;
}

.swiss-cell h2 {
  font-size: 0.95rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 16px 0 12px;
}

.swiss-cell ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.swiss-cell li {
  font-size: 0.85rem;
  color: #666;
  padding: 4px 0;
  border-bottom: 1px solid #eee;
}

.swiss-showcase {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  max-width: 1200px;
  margin: 0 auto;
  padding: 64px 32px;
  gap: 48px;
}

.swiss-showcase img {
  width: 100%;
  display: block;
  margin-bottom: 16px;
  filter: grayscale(30%);
}

.swiss-showcase h3 {
  font-size: 1rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin: 0 0 8px;
}

.swiss-showcase p {
  font-size: 0.9rem;
  line-height: 1.6;
  color: #444;
  margin: 0;
}

.swiss-footer {
  border-top: 3px solid #111;
  padding: 48px 32px;
  text-align: center;
}

.swiss-footer > span {
  display: block;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  margin-bottom: 24px;
}

.swiss-footer__logos {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 32px;
  align-items: center;
}

.swiss-footer__logos img {
  max-height: 48px;
  max-width: 200px;
}

@media (max-width: 900px) {
  .swiss-hero__grid,
  .swiss-grid {
    grid-template-columns: 1fr 1fr;
  }
  .swiss-cell:nth-child(2) {
    border-right: none;
  }
  .swiss-showcase {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 500px) {
  .swiss-hero__grid,
  .swiss-grid {
    grid-template-columns: 1fr;
  }
  .swiss-cell {
    border-right: none;
    border-bottom: 1px solid #111;
  }
}
</style>
